import { useCallback, useMemo, useState } from 'react'
import { Waves, ShieldBan } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, BulkAction, Selection } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchUsers, banUser, type AdminUserRow } from '@/lib/adapters'
import { BAN_STR as S } from './strikesApi'

/**
 * A8.08 — Ommaviy cheklov (spam to'lqini). JONLI: REAL foydalanuvchilar
 * (`/admin/users`) ustidan ishlaydi — bir necha akkauntni belgilab, ommaviy
 * ravishda bloklaydi (`/admin/users/:id/ban` bo'yicha aylanadi). Sabab MAJBURIY
 * (server ham talab qiladi + auditga yozadi). «Filtr bo'yicha hammasi» rejimi
 * qo'llab-quvvatlanmaydi — aniq tanlov talab qilinadi (xatolik radiusini cheklash).
 */
export function SpamWaveScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [reason, setReason] = useState('')

  const runBulk = useCallback(
    async (sel: Selection<AdminUserRow>) => {
      if (sel.mode !== 'rows' || sel.rows.length === 0) {
        toast(S.selectRowsFirst, { tone: 'warning' })
        return
      }
      const trimmed = reason.trim()
      if (!trimmed) {
        toast(S.reasonRequired, { tone: 'warning' })
        return
      }
      const rows = sel.rows
      if (!window.confirm(`${S.massRestrict} (${rows.length})?`)) return
      const results = await Promise.allSettled(rows.map((r) => banUser(r.id, trimmed)))
      const ok = results.filter((x) => x.status === 'fulfilled').length
      const failed = results.length - ok
      toast(failed === 0 ? `${S.applied} (${ok})` : `${S.massPartial}: ${ok}/${results.length}`, {
        tone: failed === 0 ? 'success' : 'warning',
      })
      setReloadKey((k) => k + 1)
    },
    [reason, toast],
  )

  const bulkActions = useMemo<BulkAction<AdminUserRow>[]>(
    () => [
      {
        id: 'mass-restrict',
        label: S.massRestrict,
        icon: <ShieldBan size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: (sel) => void runBulk(sel),
      },
    ],
    [runBulk],
  )

  const columns = useMemo<AdminColumn<AdminUserRow>[]>(
    () => [
      {
        key: 'username',
        header: S.user,
        type: 'text',
        minWidth: 200,
        render: (r) => {
          const name = [r.first_name, r.last_name].filter(Boolean).join(' ')
          return <span>{r.username ? `@${r.username}` : name || `#${r.id}`}</span>
        },
      },
      { key: 'phone', header: S.phone, type: 'text', width: 160 },
      { key: 'status', header: S.status, type: 'text', width: 120 },
      { key: 'created_at', header: S.registered, type: 'date', width: 150 },
      { key: 'last_seen', header: S.lastSeen, type: 'date', width: 150 },
    ],
    [],
  )

  const fetcher = useCallback(fetchUsers, [])
  return (
    <>
      <PageHeader title={S.spamWaves} screenId="A8.08" description={S.spamWavesNote} />
      <div className="flex flex-wrap items-center gap-2 p-4 pb-0">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={S.reason}
          className="min-w-[240px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
        />
        <span className="text-xs text-tertiary">{S.spamWavesNote}</span>
      </div>
      <DataTable<AdminUserRow>
        key={reloadKey}
        screenId="A8.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        selectable
        bulkActions={bulkActions}
        exportable
        emptyState={{ icon: <Waves size={28} strokeWidth={1.5} />, title: S.spamWaves }}
      />
    </>
  )
}
